import {
  BaseInput,
  Button,
  ButtonVariant,
  Select,
  Show
} from "shared/components";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { CombinedIncomeLayout } from "../components/Layout";
import { COMBINED_INCOME_FORM_ID } from "../constants";
import { toast } from "react-toastify";
import { CombinedIncomeSubStep } from "../../../constants";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { object, string } from "yup";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";
import { CombinedIncomeMode } from "../types";
import { buildCreateIncomePayload } from "../utils";

type PreTaxIncomeFormData = {
  amount: string;
  pay_period: string;
};

const validationSchema = object({
  amount: string().required("Pre-tax income is required"),
  pay_period: string().required("Pay period is required")
});

const payPeriodOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Annually", value: "annually" },
  { label: "Biweekly", value: "bi_weekly" },
  { label: "Weekly", value: "weekly" }
];

export const PreTaxIncomeStep = () => {
  const {
    updateStepMutation,
    createIncomeMutation,
    getNextStep,
    stepData,
    refetchVerification,
    mode
  } = useCombinedIncome();
  const isManualApplicationFlow = mode === CombinedIncomeMode.ManualApplication;

  const { values, register, onSubmit } = useFormTyped<PreTaxIncomeFormData>(
    {
      amount: stepData.amount || "",
      pay_period: stepData.pay_period || "monthly"
    },
    validationSchema
  );

  const handleSubmit = async (formValues: PreTaxIncomeFormData) => {
    try {
      const payload = buildCreateIncomePayload(stepData, formValues);

      const createdIncome = await createIncomeMutation.mutateAsync(payload);

      const nextStep = getNextStep(CombinedIncomeSubStep.PreTaxIncome, {
        incomeType: stepData.currentIncomeType
      });

      if (!nextStep) return;

      await updateStepMutation.mutateAsync({
        lastStep: nextStep,
        currentIncomeId: createdIncome.id,
        pay_period: formValues.pay_period,
        amount: formValues.amount
      });

      refetchVerification();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to save income details");
    }
  };

  const isValid = values.amount && values.pay_period;
  const isLoading =
    createIncomeMutation.isPending || updateStepMutation.isPending;

  return (
    <form
      id={COMBINED_INCOME_FORM_ID}
      onSubmit={onSubmit({ onSuccess: handleSubmit })}
    >
      <CombinedIncomeLayout
        title="Add pre-tax income"
        footer={
          <Button
            className="w-[125px]"
            type="submit"
            form={COMBINED_INCOME_FORM_ID}
            loading={isLoading}
            variant={ButtonVariant.PRIMARY}
            disabled={!isValid}
          >
            Continue
          </Button>
        }
      >
        <Show if={isManualApplicationFlow}>
          <p className="mb-1 text-content-700 text-sm font-medium">
            Pre-tax income
          </p>
        </Show>
        <div className={isManualApplicationFlow ? "flex gap-2" : ""}>
          <div className="w-full">
            <Select
              nativeMobileSelect
              id="pay_period"
              {...register("pay_period")}
              options={payPeriodOptions}
              placeholder="Month"
              required
              legacyErrorMargins={false}
              label={isManualApplicationFlow ? undefined : "Payment frequency"}
            />
          </div>
          <div className="w-full">
            <BaseInput
              id="amount"
              parser={moneyCentsParser({ asString: true })}
              prefix="$"
              placeholder="60,000"
              {...register("amount")}
              required
              label={isManualApplicationFlow ? undefined : "Pre-tax income"}
              className={isManualApplicationFlow ? "" : "mt-3"}
            />
          </div>
        </div>
      </CombinedIncomeLayout>
    </form>
  );
};
